// Phase MHM-1 + MHM-5.2 · Micro hub management.
// MHM-5.2 — Status column with badges (pending/approved/rejected).
// Pending rows: operational actions disabled. Rejected rows: inline reason
// + Withdraw + Resubmit. Create modal: "Submit for approval" CTA + banner.
import { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw, Plus, ToggleLeft, ToggleRight, X,
         Clock, CheckCircle2, AlertOctagon, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import MHMSubNav from "../../components/hub/MHMSubNav";
import RoleGate from "../../components/RoleGate";

function CreateModal({ open, onClose, onCreated, hubLeads, presets = null }) {
  const [name, setName] = useState(presets?.name || "");
  const [areaName, setAreaName] = useState(presets?.area_name || "");
  const [pincodes, setPincodes] = useState((presets?.area_pincodes || []).join(", "));
  const [capacity, setCapacity] = useState(presets?.capacity_parcels_per_day || 100);
  const [leadUserId, setLeadUserId] = useState(presets?.lead_user_id || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (presets) {
      setName(presets.name || ""); setAreaName(presets.area_name || "");
      setPincodes((presets.area_pincodes || []).join(", "));
      setCapacity(presets.capacity_parcels_per_day || 100);
      setLeadUserId(presets.lead_user_id || "");
    }
  }, [presets]);

  if (!open) return null;
  const submit = async () => {
    if (!name) return toast.error("Name is required");
    setSaving(true);
    try {
      const body = { name, area_name: areaName,
        area_pincodes: pincodes.split(",").map((s) => s.trim()).filter(Boolean),
        capacity_parcels_per_day: Number(capacity) || 0,
        lead_user_id: leadUserId || null };
      const { data } = await api.post("/hub/micro-hubs", body);
      toast.success(data.needs_approval
        ? "Submitted for admin approval"
        : "Micro hub created");
      onCreated(); onClose();
    } catch (e) { toast.error(e.userMessage || "Submit failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="mhm-microhub-create-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-['Outfit'] text-lg font-semibold">
            {presets ? "Resubmit micro hub" : "Create micro hub"}
          </h3>
          <button onClick={onClose} data-testid="mhm-microhub-create-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Name</label>
            <input data-testid="mhm-microhub-create-name" value={name} onChange={(e) => setName(e.target.value)}
                   className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Area</label>
            <input data-testid="mhm-microhub-create-area" value={areaName} onChange={(e) => setAreaName(e.target.value)}
                   className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Pincodes (comma-separated)</label>
            <input data-testid="mhm-microhub-create-pincodes" value={pincodes} onChange={(e) => setPincodes(e.target.value)}
                   placeholder="560001, 560002" className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Capacity (parcels/day)</label>
            <input data-testid="mhm-microhub-create-capacity" type="number" value={capacity}
                   onChange={(e) => setCapacity(e.target.value)}
                   className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Lead (micro_hub_lead user)</label>
            <select data-testid="mhm-microhub-create-lead" value={leadUserId} onChange={(e) => setLeadUserId(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-2 py-1.5">
              <option value="">— None —</option>
              {hubLeads.map((u) => (<option key={u.id} value={u.id}>{u.name || u.email}</option>))}
            </select>
          </div>
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2"
               data-testid="mhm-microhub-create-banner">
            This request will be sent to admin for approval. You'll be notified by email once decided.
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-sm px-3 py-1.5 border border-slate-200 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={saving}
                  data-testid="mhm-microhub-create-submit"
                  className="text-sm px-3 py-1.5 bg-[#FF6B00] hover:bg-[#E55F00] text-white rounded-lg disabled:opacity-60">
            {saving ? "Submitting…" : "Submit for approval"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status, reason }) {
  if (status === "pending_admin_approval") {
    return (
      <span data-testid="mhm-microhub-status-pending"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold
                       bg-amber-50 text-amber-700 border border-amber-200">
        <Clock className="h-3 w-3"/> Pending approval
      </span>
    );
  }
  if (status === "rejected") {
    return (
      <span data-testid="mhm-microhub-status-rejected"
            title={reason || "Rejected by admin"}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold
                       bg-rose-50 text-rose-700 border border-rose-200">
        <AlertOctagon className="h-3 w-3"/> Rejected
      </span>
    );
  }
  return (
    <span data-testid="mhm-microhub-status-approved"
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold
                     bg-orange-50 text-[#C2410C] border border-orange-200">
      <CheckCircle2 className="h-3 w-3"/> Approved
    </span>
  );
}

export default function MicroHubs() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [resubmit, setResubmit] = useState(null);
  const [leads, setLeads] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hub/micro-hubs");
      setRows(data.rows || []);
    } catch (e) { toast.error(e.userMessage || "Couldn't load micro hubs"); }
    finally { setLoading(false); }
  }, []);

  const loadLeads = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users?role=micro_hub_lead");
      setLeads(data.rows || data.users || []);
    } catch { setLeads([]); }
  }, []);

  useEffect(() => { load(); loadLeads(); }, [load, loadLeads]);

  const toggleActive = async (h) => {
    try {
      await api.patch(`/hub/micro-hubs/${h.hub_id}`, { is_active: !h.is_active });
      toast.success(`${h.name} ${!h.is_active ? "activated" : "deactivated"}`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && detail.error === "micro_hub_not_yet_approved") {
        toast.error("Awaiting admin approval — edits are blocked");
      } else { toast.error(e.userMessage || "Update failed"); }
    }
  };

  const withdraw = async (h) => {
    if (!window.confirm(`Withdraw the request for "${h.name}"?`)) return;
    try {
      await api.delete(`/hub/micro-hubs/${h.hub_id}`);
      toast.success("Request withdrawn"); load();
    } catch (e) { toast.error(e.userMessage || "Withdraw failed"); }
  };

  return (
    <RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}>
      <MHMSubNav />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="mhm-microhubs-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit']" data-testid="mhm-microhubs-title">
              Micro Hubs
            </h1>
            <p className="text-sm text-slate-500">Micro hubs under your main hub · {rows.length}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} data-testid="mhm-microhubs-refresh"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 hover:bg-slate-50 rounded-lg">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
            </button>
            <button onClick={() => { setResubmit(null); setCreateOpen(true); }}
                    data-testid="mhm-microhubs-create"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#FF6B00] hover:bg-[#E55F00] text-white rounded-lg">
              <Plus className="h-4 w-4" /> New micro hub
            </button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="mhm-microhubs-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Hub ID</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Lead</th>
                <th className="px-3 py-2 text-left">Orders today</th>
                <th className="px-3 py-2 text-left w-44">Utilization</th>
                <th className="px-3 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && !loading && (
                <tr><td colSpan={7} className="text-center py-10 text-slate-400">No micro hubs yet</td></tr>
              )}
              {rows.map((h) => {
                const status = h.approval_status || "approved";
                const blocked = status !== "approved";
                return (
                  <tr key={h.hub_id} data-testid={`mhm-microhubs-row-${h.hub_id}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-xs">{h.hub_id}</td>
                    <td className="px-3 py-2">{h.name}
                      {status === "rejected" && h.rejected_reason && (
                        <div className="text-xs text-rose-600 italic mt-0.5"
                             data-testid={`mhm-microhubs-reject-reason-${h.hub_id}`}>
                          Reason: {h.rejected_reason}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2"><StatusBadge status={status} reason={h.rejected_reason}/></td>
                    <td className="px-3 py-2 text-xs text-slate-600">{h.lead_email || "—"}</td>
                    <td className="px-3 py-2">{h.orders_handled_today ?? 0}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded overflow-hidden">
                          <div className={`h-1.5 ${h.utilization_pct > 90 ? "bg-rose-500" : h.utilization_pct > 60 ? "bg-amber-500" : "bg-green-500"}`}
                               style={{ width: `${Math.min(100, h.utilization_pct || 0)}%` }}/>
                        </div>
                        <span className="text-xs text-slate-500 w-12 text-right">{h.utilization_pct || 0}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 space-x-2">
                      <button onClick={() => toggleActive(h)} disabled={blocked}
                              title={blocked ? "Awaiting admin approval" : ""}
                              data-testid={`mhm-microhubs-toggle-${h.hub_id}`}
                              className={`inline-flex items-center gap-1 text-xs ${blocked ? "text-slate-300 cursor-not-allowed" : "text-slate-600 hover:text-slate-900"}`}>
                        {h.is_active ? <ToggleRight className="h-5 w-5 text-green-600" /> : <ToggleLeft className="h-5 w-5 text-slate-400" />}
                        {h.is_active ? "Active" : "Inactive"}
                      </button>
                      {status === "pending_admin_approval" && (
                        <button onClick={() => withdraw(h)}
                                data-testid={`mhm-microhubs-withdraw-${h.hub_id}`}
                                className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900">
                          <Undo2 className="h-3.5 w-3.5"/> Withdraw
                        </button>
                      )}
                      {status === "rejected" && (
                        <>
                          <button onClick={() => withdraw(h)}
                                  data-testid={`mhm-microhubs-delete-${h.hub_id}`}
                                  className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800">
                            Delete
                          </button>
                          <button onClick={() => { setResubmit({
                                    name: h.name, area_name: h.area_name,
                                    area_pincodes: h.area_pincodes,
                                    capacity_parcels_per_day: h.capacity_parcels_per_day,
                                    lead_user_id: h.manager_id }); setCreateOpen(true); }}
                                  data-testid={`mhm-microhubs-resubmit-${h.hub_id}`}
                                  className="inline-flex items-center gap-1 text-xs text-[#FF6B00] hover:text-[#E55F00]">
                            Resubmit
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <CreateModal open={createOpen} onClose={() => setCreateOpen(false)}
                   onCreated={load} hubLeads={leads} presets={resubmit}/>
    </RoleGate>
  );
}
